import { IDeviceAdapter } from './base';
import { HikvisionAdapter } from './hikvision';
import { ControlIDAdapter } from './controlid';
import { RFIDAdapter } from './rfid';

/**
 * Adapter factory
 * Returns the appropriate vendor adapter based on device vendor
 *
 * Usage:
 * ```typescript
 * const adapter = AdapterFactory.getAdapter('hikvision');
 * const payload = adapter.formatSyncPersonPayload(personData);
 * ```
 */
export class AdapterFactory {
  private static adapters: Map<string, IDeviceAdapter> = new Map<string, IDeviceAdapter>([
    ['hikvision', new HikvisionAdapter()],
    ['controlid', new ControlIDAdapter()],
    ['rfid', new RFIDAdapter()],
  ]);

  /**
   * Get adapter for device vendor
   * @param vendor - Device vendor (case-insensitive)
   * @throws Error if no adapter found for vendor
   */
  static getAdapter(vendor: string): IDeviceAdapter {
    const normalizedVendor = vendor.toLowerCase();
    const adapter = this.adapters.get(normalizedVendor);

    if (!adapter) {
      throw new Error(`No adapter found for vendor: ${vendor}. Available: ${Array.from(this.adapters.keys()).join(', ')}`);
    }

    return adapter;
  }

  /**
   * Check if vendor is supported
   */
  static hasAdapter(vendor: string): boolean {
    return this.adapters.has(vendor.toLowerCase());
  }

  /**
   * Get list of supported vendors
   */
  static getSupportedVendors(): string[] {
    return Array.from(this.adapters.keys());
  }
}

// Export all adapters and types for direct use if needed
export type { IDeviceAdapter, PersonData, DeviceInfo } from './base';
export { DeviceOperation } from './base';
export { HikvisionAdapter } from './hikvision';
export { ControlIDAdapter } from './controlid';
export { RFIDAdapter } from './rfid';

// Export session types
export type {
  SessionInfo,
  FirmwareInfo,
  SessionEstablishmentRequest,
  SessionEstablishmentResult,
} from '../session/types';
export { sessionManager } from '../session/SessionManager';
